import { useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';

/**
 * Full-bleed background for the parallax band. Static single image — no
 * slideshow rotation.
 */
const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Relax, you’re home',
    },
  },
};

/** Primary (filled) call-to-action. */
const primaryCtaDef: SlotGroupDefinition = {
  label: 'Primary Button',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/schedule-a-tour',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Schedule A Visit',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

/** Secondary (outlined) call-to-action. */
const secondaryCtaDef: SlotGroupDefinition = {
  label: 'Secondary Button',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/virtual-tour',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Take A Virtual Tour',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function ParallaxBanner() {
  const bg = useSlotGroup('parallaxbanner.bg_image', bgImageDef);
  const primaryCta = useSlotGroup('parallaxbanner.primary_cta', primaryCtaDef);
  const secondaryCta = useSlotGroup('parallaxbanner.secondary_cta', secondaryCtaDef);

  // Display fonts: a classic serif for the headline and a script face for the
  // tagline. Injected the same way the Header loads its heading font, and
  // guarded so the links are only added once per document.
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="parallax-display"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Josefin+Slab:wght@400;500;600;700&family=Dancing+Script:wght@500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'parallax-display');
    document.head.appendChild(link);
  }, []);

  return (
    <section
      className="parallax-band relative w-full bg-white px-3 sm:px-5 md:px-6 py-3 sm:py-5 md:py-6"
    >
      {/* Boxed frame: a white border is created by the section's padding, the
          same boxed treatment the Hero uses. The inner box clips the pinned
          background to the frame. */}
      <div className="parallax-box relative w-full overflow-hidden">
      {/* Static pinned background. Uses CSS `background-attachment: fixed`, so
          the browser keeps the image stationary against the viewport while the
          box scrolls over it — handled entirely on the compositor, with no
          JS/scroll handler, so the edges never glitch. On touch devices (which
          don't support fixed backgrounds) it falls back to a normal cover
          image; see the media query in the <style> block below. */}
      <div className="absolute inset-0 z-0">
        <EditableSlotGroup groupPath="parallaxbanner.bg_image" definition={bgImageDef}>
          <div
            className="parallax-layer absolute inset-0"
            role="img"
            aria-label={bg.alt || ''}
            style={{
              backgroundImage: bg.image ? `url("${bg.image}")` : undefined,
            }}
          />
        </EditableSlotGroup>
      </div>

      {/* Brand theme-color tint overlay for legibility. Opacity is applied via
          inline style because Tailwind's `/NN` modifier doesn't apply to an
          arbitrary CSS-variable color. */}
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{ backgroundColor: 'var(--vibe-color-primary)', opacity: 0.4 }}
      />

      {/* Centered content. */}
      <div className="relative z-10 flex min-h-[clamp(380px,60vh,640px)] flex-col items-center justify-center px-6 py-16 text-center text-white">
        <RichTextContent
          path="parallaxbanner.title"
          label="Title"
          defaultValue="<p>RELAX</p>"
          as="h2"
          className="parallax-title -translate-x-[6%] sm:-translate-x-[8%] md:-translate-x-[10%] text-7xl sm:text-8xl md:text-9xl lg:text-[10.5rem] font-bold uppercase leading-none tracking-tight drop-shadow-lg"
        />
        <RichTextContent
          path="parallaxbanner.subtitle"
          label="Subtitle"
          defaultValue="<p>you’re home</p>"
          as="p"
          className="parallax-subtitle -mt-6 sm:-mt-8 md:-mt-10 translate-x-[32%] sm:translate-x-[42%] md:translate-x-[50%] text-5xl sm:text-6xl md:text-7xl drop-shadow-md"
        />

        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:gap-5">
          <EditableSlotGroup groupPath="parallaxbanner.primary_cta" definition={primaryCtaDef}>
            <a
              href={primaryCta.url || '/schedule-a-tour'}
              target={primaryCta._target || '_self'}
              className="parallax-cta parallax-cta--solid inline-flex items-center justify-center min-w-[17rem] whitespace-nowrap px-8 py-3.5 text-sm font-medium tracking-[0.18em] transition-all duration-300"
            >
              {primaryCta.label || 'Schedule A Visit'}
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="parallaxbanner.secondary_cta" definition={secondaryCtaDef}>
            <a
              href={secondaryCta.url || '/virtual-tour'}
              target={secondaryCta._target || '_self'}
              className="parallax-cta parallax-cta--outline inline-flex items-center justify-center min-w-[17rem] whitespace-nowrap px-8 py-3.5 text-sm font-medium tracking-[0.18em] text-white transition-all duration-300"
            >
              {secondaryCta.label || 'Take A Virtual Tour'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>
      </div>

      <style>{`
        .parallax-layer {
          background-position: center;
          background-size: cover;
          background-repeat: no-repeat;
          /* Pin the image to the viewport; the box's overflow clips it to the
             frame. The browser composites this, so it never lags or tears. */
          background-attachment: fixed;
        }
        /* Touch devices generally don't support fixed backgrounds (iOS Safari
           ignores it and others mis-size it), so fall back to a normal cover
           image that scrolls with the box. */
        @media (hover: none), (pointer: coarse) {
          .parallax-layer {
            background-attachment: scroll;
          }
        }
        .parallax-title {
          font-family: "Josefin Slab", Rockwell, "Courier New", Georgia, serif;
        }
        .parallax-subtitle {
          font-family: "Dancing Script", "Brush Script MT", cursive;
          font-weight: 600;
        }
        .parallax-cta--solid {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary, #fff);
          border: 1.5px solid var(--vibe-color-secondary);
        }
        .parallax-cta--solid:hover {
          filter: brightness(1.06);
          transform: translateY(-2px);
        }
        .parallax-cta--outline {
          background: transparent;
          border: 1.5px solid rgba(255, 255, 255, 0.85);
        }
        .parallax-cta--outline:hover {
          background: rgba(255, 255, 255, 0.12);
          transform: translateY(-2px);
        }
        @media (prefers-reduced-motion: reduce) {
          .parallax-cta:hover { transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ParallaxBanner',
  displayName: 'Parallax Banner',
  tokens: {
    'parallaxbanner.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>RELAX</p>',
    },
    'parallaxbanner.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>you’re home</p>',
    },
  },
  slotGroups: {
    'parallaxbanner.bg_image': bgImageDef,
    'parallaxbanner.primary_cta': primaryCtaDef,
    'parallaxbanner.secondary_cta': secondaryCtaDef,
  },
};

(ParallaxBanner as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ParallaxBanner;
